import { get, post, del } from '../api';
import { showToast } from '../components/toast';
import { state } from '../state';
import { mountNamedGuestDiagnostics } from '../components/named-guest-controls';
import { SANDBOXES, sandboxHref } from './sandboxes';

interface StagedInfo {
  has_staged: boolean;
  files: { path: string; size: number }[];
  total_size: number;
}

interface Build {
  id: string;
  app: string;
  version: string;
  git_hash: string;
  creator: string;
  notes: string;
  changelog: string;
  status: string;
  build_path: string;
  file_count: number;
  total_size: number;
  created_at: string;
  promoted_at: string | null;
  deployed_at: string | null;
  archived_at: string | null;
  promoted_from: string | null;
}

type ReviewTokenPurpose = 'test_ui_review' | 'ai_review';

interface ScopedToken {
  id: string;
  token: string;
  url: string;
  purpose: string;
  server_id: string;
  created_by: string | null;
  status: string;
  max_uses: number;
  use_count: number;
  linked_record_id: string | null;
  linked_record_type: string | null;
  metadata: string;
  expires_at: string | null;
  used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

interface CreateScopedTokenResponse {
  id: string;
  token: string;
  url: string;
  purpose: string;
  status: string;
  max_uses: number;
  use_count: number;
  expires_at: string | null;
  created_at: string;
}

interface Deploy {
  id: string;
  app: string;
  git_hash: string;
  changed_files: string;
  asset_hashes: string;
  backup_path: string;
  status: string;
  deployed_at: string;
  approved_at: string | null;
  reverted_at: string | null;
  archived_at: string | null;
  notes: string;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let showArchived = false;
let showArchivedBuilds = false;
let activeTab: 'deploys' | 'builds' | 'preflight' | 'tunnels' = 'deploys';

export function cleanupDevelopment(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  if (tunnelPollTimer) { clearInterval(tunnelPollTimer); tunnelPollTimer = null; }
  showArchived = false;
  showArchivedBuilds = false;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function fmtDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', {
    timeZone: 'Australia/Sydney',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function statusPill(status: string): string {
  const colors: Record<string, string> = {
    pending: 'var(--warning)',
    approved: 'var(--success)',
    reverted: 'var(--danger)',
    archived: 'var(--fg-muted)',
  };
  const color = colors[status] || 'var(--fg-muted)';
  return `<span class="deploy-status-pill" style="background:${color}">${esc(status)}</span>`;
}

function renderDeployCard(d: Deploy): string {
  const files: string[] = JSON.parse(d.changed_files || '[]');
  const fileCount = files.length;
  const hashShort = d.git_hash ? d.git_hash.slice(0, 8) : 'no hash';
  const isPending = d.status === 'pending';
  const isApproved = d.status === 'approved';
  const isReverted = d.status === 'reverted';

  const actions = [];
  if (isPending) {
    actions.push(`<button class="btn btn-sm deploy-action-btn deploy-go-live-btn dev-btn-success" data-deploy-id="${esc(d.id)}" data-action="deploy">Deploy Now</button>`);
    actions.push(`<button class="btn btn-sm deploy-action-btn deploy-approve-btn" data-deploy-id="${esc(d.id)}" data-action="approve">Approve</button>`);
    actions.push(`<button class="btn btn-sm btn-danger-ghost deploy-action-btn" data-deploy-id="${esc(d.id)}" data-action="revert">Revert</button>`);
  }
  if (isApproved) {
    actions.push(`<button class="btn btn-sm deploy-action-btn deploy-go-live-btn dev-btn-success" data-deploy-id="${esc(d.id)}" data-action="deploy">Deploy Now</button>`);
    actions.push(`<button class="btn btn-sm deploy-action-btn deploy-archive-btn" data-deploy-id="${esc(d.id)}" data-action="archive">Archive</button>`);
  }
  if (isReverted) {
    actions.push(`<button class="btn btn-sm deploy-action-btn deploy-archive-btn" data-deploy-id="${esc(d.id)}" data-action="archive">Archive</button>`);
  }

  const fileList = fileCount > 0
    ? `<div class="deploy-files">${files.slice(0, 8).map(f => `<div class="deploy-file">${esc(f)}</div>`).join('')}${fileCount > 8 ? `<div class="deploy-file deploy-file-more">+${fileCount - 8} more</div>` : ''}</div>`
    : '';

  const timeline = [];
  timeline.push(`<div class="deploy-timeline-item">Deployed ${esc(fmtDate(d.deployed_at))}</div>`);
  if (d.approved_at) timeline.push(`<div class="deploy-timeline-item deploy-timeline-approved">Approved ${esc(fmtDate(d.approved_at))}</div>`);
  if (d.reverted_at) timeline.push(`<div class="deploy-timeline-item deploy-timeline-reverted">Reverted ${esc(fmtDate(d.reverted_at))}</div>`);

  return `
    <div class="deploy-card deploy-card-${esc(d.status)}" data-deploy-id="${esc(d.id)}">
      <div class="deploy-card-header">
        <div class="deploy-card-title">
          ${statusPill(d.status)}
          <span class="deploy-card-app">${esc(d.app)}</span>
          <span class="deploy-card-hash">${esc(hashShort)}</span>
        </div>
        <div class="deploy-card-actions">${actions.join('')}</div>
      </div>
      ${d.notes ? `<div class="deploy-card-notes">${esc(d.notes)}</div>` : ''}
      <div class="deploy-card-meta">
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Files</span>
          <span class="deploy-meta-value">${fileCount} changed</span>
        </div>
        <div class="deploy-timeline">${timeline.join('')}</div>
      </div>
      ${fileList}
    </div>
  `;
}

async function loadDeploys(): Promise<Deploy[]> {
  const endpoint = showArchived ? '/api/deploys/all' : '/api/deploys';
  return get<Deploy[]>(endpoint);
}

async function paint(container: HTMLElement): Promise<void> {
  try {
    const deploys = await loadDeploys();
    if (!document.getElementById('dev-deploy-list')) return;

    if (deploys.length === 0) {
      container.innerHTML = `<div class="deploy-empty">No deploys yet. Run the deploy script to get started.</div>`;
      return;
    }

    container.innerHTML = deploys.map(renderDeployCard).join('');
    wireActions(container);
  } catch {
    container.innerHTML = `<div class="deploy-empty">Failed to load deploys.</div>`;
  }
}

function wireActions(container: HTMLElement): void {
  container.querySelectorAll<HTMLButtonElement>('.deploy-action-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = btn.dataset['deployId'] || '';
      const action = btn.dataset['action'] || '';
      if (!id || !action) return;
      btn.disabled = true;
      try {
        if (action === 'deploy') {
          if (!confirm('Deploy staged files to live? This replaces the current frontend.')) {
            btn.disabled = false;
            return;
          }
          await post(`/api/deploys/${encodeURIComponent(id)}/deploy`, {});
          showToast('Deployed to live!', 'ok');
        } else if (action === 'approve') {
          await post(`/api/deploys/${encodeURIComponent(id)}/approve`, { notes: '' });
          showToast('Deploy approved', 'ok');
        } else if (action === 'revert') {
          await post(`/api/deploys/${encodeURIComponent(id)}/revert`, { notes: '' });
          showToast('Deploy reverted', 'ok');
        } else if (action === 'archive') {
          await post(`/api/deploys/${encodeURIComponent(id)}/archive`, {});
          showToast('Archived', 'ok');
        }
        const list = document.getElementById('dev-deploy-list');
        if (list) void paint(list);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Action failed', 'err');
        btn.disabled = false;
      }
    });
  });
}

export async function renderDevelopment(body: HTMLElement): Promise<void> {
  cleanupDevelopment();

  body.innerHTML = `
    <div class="deploy-pane" style="display:flex;flex-direction:column;height:100%">
      <div class="dev-tabs" id="dev-tabs">
        <button class="dev-tab ${activeTab === 'deploys' ? 'dev-tab-active' : ''}" data-tab="deploys">Deploys</button>
        <button class="dev-tab ${activeTab === 'builds' ? 'dev-tab-active' : ''}" data-tab="builds">Test Builds & Links</button>
        <button class="dev-tab ${activeTab === 'preflight' ? 'dev-tab-active' : ''}" data-tab="preflight">Preflight</button>
        <button class="dev-tab ${activeTab === 'tunnels' ? 'dev-tab-active' : ''}" data-tab="tunnels">Tunnels</button>
      </div>
      <div id="dev-tab-content" style="flex:1;min-height:0;overflow-y:auto"></div>
    </div>
  `;

  const tabsEl = document.getElementById('dev-tabs') as HTMLElement;
  const contentEl = document.getElementById('dev-tab-content') as HTMLElement;

  function renderActiveTab(): void {
    if (activeTab === 'deploys') {
      renderDeploysTab(contentEl);
    } else if (activeTab === 'builds') {
      renderBuildsTab(contentEl);
    } else if (activeTab === 'tunnels') {
      renderTunnelsTab(contentEl);
    } else {
      renderPreflightTab(contentEl);
    }
  }

  tabsEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.dev-tab') as HTMLElement | null;
    if (!btn) return;
    const tab = btn.dataset['tab'] as 'deploys' | 'builds' | 'preflight' | 'tunnels';
    if (tab === activeTab) return;
    activeTab = tab;
    tabsEl.querySelectorAll('.dev-tab').forEach(b => b.classList.remove('dev-tab-active'));
    btn.classList.add('dev-tab-active');
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    renderActiveTab();
  });

  renderActiveTab();
}

function renderDeploysTab(container: HTMLElement): void {
  container.innerHTML = `
    <div id="dev-staged-preview" class="deploy-staged-preview"></div>
    <div class="deploy-toolbar">
      <h2 class="deploy-heading">Deploys</h2>
      <div class="deploy-toolbar-right">
        <label class="deploy-toggle-archived">
          <input type="checkbox" id="dev-show-archived" ${showArchived ? 'checked' : ''} />
          Show archived
        </label>
        <button class="btn btn-secondary btn-sm" id="dev-refresh-btn" type="button">Refresh</button>
      </div>
    </div>
    <div class="deploy-list" id="dev-deploy-list">
      <div class="deploy-empty">Loading...</div>
    </div>
  `;

  void loadStagedPreview();

  const list = document.getElementById('dev-deploy-list');
  if (list) void paint(list);

  document.getElementById('dev-refresh-btn')?.addEventListener('click', () => {
    if (list) void paint(list);
  });

  document.getElementById('dev-show-archived')?.addEventListener('change', (e) => {
    showArchived = (e.target as HTMLInputElement).checked;
    if (list) void paint(list);
  });

  pollTimer = setInterval(() => {
    if (!document.getElementById('dev-deploy-list')) {
      if (pollTimer) clearInterval(pollTimer);
      return;
    }
    if (list) void paint(list);
    void loadStagedPreview();
  }, 15_000);
}

/* ── Test Builds tab ─────────────────────────────────────────────── */

const BUILD_STATUS_COLORS: Record<string, string> = {
  test: 'var(--accent)',
  staging: 'var(--warning)',
  live: 'var(--success)',
  archived: 'var(--fg-muted)',
};

function renderBuildsTab(container: HTMLElement): void {
  container.innerHTML = `
    <div class="dev-test-next">
      <div>
        <h2>What to test next</h2>
        <p>Start with the highest-risk test surfaces before reviewing supporting reports and repository state.</p>
      </div>
      <div class="dev-test-next-links">${renderWhatToTestNext()}</div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Active Test Builds</h2>
          <p>Generated test build bundles served under auth-gated /people/test routes.</p>
        </div>
        <div class="deploy-toolbar-right">
          <label class="deploy-toggle-archived">
            <input type="checkbox" id="builds-show-archived" ${showArchivedBuilds ? 'checked' : ''} />
            Show archived
          </label>
          <button class="btn btn-secondary btn-sm" id="builds-refresh-btn" type="button">Refresh</button>
        </div>
      </div>
      <div id="builds-list" class="deploy-list">
        <div class="deploy-empty">Loading...</div>
      </div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Test Sandboxes</h2>
          <p>Isolated full-app preview sandboxes. Auth-gated; opens behind your Hub Core session.</p>
        </div>
      </div>
      <div class="dev-card-grid">${renderSandboxCards()}</div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Quick Test Links</h2>
          <p>Direct routes for the main test-only screens and read-only review tools.</p>
        </div>
      </div>
      <div class="dev-card-grid">${renderQuickLinkCards()}</div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Guest Access</h2>
          <p>Named communication links with isolated text and call channels.</p>
        </div>
        <div class="deploy-toolbar-right">
          <button class="btn btn-secondary btn-sm" id="guest-access-refresh" type="button">Refresh</button>
        </div>
      </div>
      <div id="guest-access-panel"><div class="deploy-empty">Loading...</div></div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Review Token Control</h2>
          <p>Create scoped review access for external reviewers without exposing general app credentials.</p>
        </div>
      </div>
      <div id="review-token-panel">${renderReviewTokenShell()}</div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Reports</h2>
          <p>Read-only implementation and verification notes for recent development work.</p>
        </div>
      </div>
      <div class="dev-card-grid">${renderReportCards()}</div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Cell Infrastructure</h2>
          <p>Manage Hub Cell HMAC secrets for status, calls, and SMS mirroring.</p>
        </div>
      </div>
      <div id="cell-infra-panel" style="padding:12px 16px">
        <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center">
          <button class="deploy-action-btn" id="btn-rotate-cell-secret" style="padding:8px 16px">Rotate Cell Status Secret</button>
          <span id="cell-rotate-status" style="font-size:12px;color:var(--fg-muted)"></span>
        </div>
        <div style="font-size:11px;color:var(--fg-muted);margin-top:8px">
          Generates a new HMAC-SHA256 secret and stores it on hub-people. If HUB_CELL_URL is set, auto-pushes to hub-cell. The secret is never shown — rotation is automatic.
        </div>
        <div id="cell-health-badges" style="margin-top:12px"></div>
      </div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Verification Status</h2>
          <p>Manual review reminders that prevent route, reload, and access-control false positives.</p>
        </div>
      </div>
      ${renderVerificationStatus()}
    </div>
  `;

  const listEl = document.getElementById('builds-list') as HTMLElement;
  void paintBuilds(listEl);

  document.getElementById('builds-refresh-btn')?.addEventListener('click', () => {
    void paintBuilds(listEl);
  });

  document.getElementById('builds-show-archived')?.addEventListener('change', (e) => {
    showArchivedBuilds = (e.target as HTMLInputElement).checked;
    void paintBuilds(listEl);
  });

  wireQuickLinkCopy(container);
  void checkSandboxHealth(container);
  wireReviewTokenPanel(container);
  mountNamedGuestDiagnostics(container);
  wireCellInfraPanel();
  pollTimer = setInterval(() => {
    if (!document.getElementById('builds-list')) {
      if (pollTimer) clearInterval(pollTimer);
      return;
    }
    void paintBuilds(listEl);
  }, 15_000);
}

function renderWhatToTestNext(): string {
  return QUICK_LINKS.slice(0, 3).map((link) => `
    <a class="dev-test-next-link" href="${esc(link.href)}" target="_blank" rel="noopener">
      <span class="${esc(badgeClass(link.badge))}">${esc(link.badge)}</span>
      <strong>${esc(link.title)}</strong>
      <span>${esc(link.description)}</span>
    </a>
  `).join('');
}

/** Auto-render one card per SANDBOXES manifest entry (registry-driven). */
function renderSandboxCards(): string {
  return SANDBOXES.map(s => {
    const href = sandboxHref(s);
    return `
      <div class="deploy-card dev-review-card">
        <div class="deploy-card-header">
          <div class="deploy-card-title">
            <span class="${esc(badgeClass('Sandbox'))}">Sandbox</span>
            <span class="deploy-card-app">${esc(s.title)}</span>
            <span class="deploy-health-badge" data-sandbox-id="${esc(s.id)}">checking...</span>
          </div>
          <div class="deploy-card-actions">
            <a class="btn btn-sm deploy-action-btn dev-review-open" href="${esc(href)}" target="_blank" rel="noopener">Open</a>
          </div>
        </div>
        <div class="deploy-card-notes">${esc(s.description)}</div>
        <div class="build-quick-links">
          <div class="build-quick-links-row">
            <span class="build-quick-links-label">Route</span>
            <code class="build-public-url" title="Click to copy" data-url="${esc(href)}">${esc(href)}</code>
          </div>
        </div>
      </div>
    `;
  }).join('');
}

/**
 * One-shot health check per sandbox card. Tries HEAD /{id}/api/health, falls
 * back to HEAD /{id}/ on network failure. 2xx/3xx → up (green); 4xx/5xx →
 * pending (red, route may not be live yet); thrown/network → offline (grey).
 */
async function checkSandboxHealth(container: HTMLElement): Promise<void> {
  const badges = container.querySelectorAll<HTMLElement>('.deploy-health-badge[data-sandbox-id]');
  for (const badge of badges) {
    const id = badge.dataset['sandboxId'] ?? '';
    if (!id) continue;
    let res: Response | null = null;
    try {
      res = await fetch(`/${encodeURIComponent(id)}/api/health`, { method: 'HEAD' });
    } catch {
      try {
        res = await fetch(`/${encodeURIComponent(id)}/`, { method: 'HEAD' });
      } catch {
        res = null;
      }
    }
    if (!res) {
      badge.textContent = 'offline';
      badge.style.background = 'var(--fg-muted)';
      badge.style.color = 'var(--bg)';
      badge.title = `No response from /${id}/ — sandbox container may be down.`;
    } else if (res.ok || (res.status >= 300 && res.status < 400)) {
      badge.textContent = 'up';
      badge.style.background = 'var(--success)';
      badge.style.color = 'var(--bg)';
    } else {
      badge.textContent = 'pending';
      badge.style.background = 'var(--danger)';
      badge.style.color = 'var(--bg)';
      badge.title = `HEAD /${id}/ returned ${res.status}. Gateway route may not be live yet.`;
    }
  }
}

function badgeClass(badge: string): string {
  const key = badge.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return `deploy-status-pill dev-badge dev-badge-${key}`;
}

function renderQuickLinkCards(): string {
  return QUICK_LINKS.map(link => `
    <div class="deploy-card dev-review-card ${link.disabled ? 'dev-card-disabled' : ''}">
      <div class="deploy-card-header">
        <div class="deploy-card-title">
          <span class="${esc(badgeClass(link.badge))}">${esc(link.badge)}</span>
          <span class="deploy-card-app">${esc(link.title)}</span>
        </div>
        <div class="deploy-card-actions">
          ${link.disabled
            ? `<button class="btn btn-sm deploy-action-btn" type="button" disabled>Coming soon</button>`
            : `<a class="btn btn-sm deploy-action-btn dev-review-open" href="${esc(link.href)}" target="_blank" rel="noopener">Open</a>`}
        </div>
      </div>
      <div class="deploy-card-notes">${esc(link.description)}</div>
      ${link.href && !link.disabled ? `
        <div class="build-quick-links">
          <div class="build-quick-links-row">
            <span class="build-quick-links-label">Route</span>
            <code class="build-public-url" title="Click to copy" data-url="${esc(link.href)}">${esc(link.href)}</code>
          </div>
        </div>
      ` : ''}
    </div>
  `).join('');
}

function renderReportCards(): string {
  return REPORT_LINKS.map(r => `
    <div class="deploy-card dev-review-card">
      <div class="deploy-card-header">
        <div class="deploy-card-title">
          <span class="${esc(badgeClass(r.badge))}">${esc(r.badge)}</span>
          <span class="deploy-card-app">${esc(r.title)}</span>
        </div>
      </div>
      <div class="deploy-card-notes">${esc(r.date)} - ${esc(r.description)}</div>
    </div>
  `).join('');
}



function renderVerificationStatus(): string {
  return `
    <div class="dev-verification-grid">
      <div class="dev-verification-item">
        <span class="build-quick-links-label">Public route groups</span>
        <strong>6</strong>
      </div>
      <div class="dev-verification-item">
        <span class="build-quick-links-label">Auth-gated control groups</span>
        <strong>2</strong>
      </div>
      <div class="dev-verification-item">
        <span class="build-quick-links-label">Scoped-token surfaces</span>
        <strong>1</strong>
      </div>
    </div>
    <div class="dev-warning-list">
      <div class="dev-warning">HTTP 200 is not success — visible UI render is required</div>
      <div class="dev-warning">Reload is a diagnostic step, not a fix</div>
      <div class="dev-warning">All test UI is auth-gated</div>
      <div class="dev-warning">External review should use scoped tokens</div>
    </div>
  `;
}

function renderReviewTokenShell(): string {
  const serverId = currentServerId();
  if (!serverId) {
    return renderReviewTokenDisabled([
      'Select a server before managing review tokens.',
      'Create time-limited test UI review and AI review links.',
      'Revoke links without exposing long-lived credentials.',
    ]);
  }

  return `
    <div class="dev-token-warning">Review tokens provide scoped external access. Time-limited and purpose-bound.</div>
    <div class="dev-token-toolbar">
      <button class="btn btn-secondary btn-sm" id="review-token-refresh" type="button">Refresh</button>
      <button class="btn btn-sm" id="review-token-show-form" type="button">Create Review Token</button>
    </div>
    <div id="review-token-created" class="dev-token-created" hidden></div>
    <div id="review-token-form" class="dev-token-form" hidden>
      <label>
        <span>Purpose</span>
        <select id="review-token-purpose">
          <option value="test_ui_review">test_ui_review</option>
          <option value="ai_review">ai_review</option>
        </select>
      </label>
      <label>
        <span>Linked route/build</span>
        <input id="review-token-linked" type="text" value="support-v2-draft" />
      </label>
      <label>
        <span>Expiry</span>
        <select id="review-token-expiry">
          <option value="24">24h</option>
          <option value="168">7d</option>
          <option value="720">30d</option>
        </select>
      </label>
      <label>
        <span>Max uses</span>
        <input id="review-token-max-uses" type="number" min="1" step="1" value="10" />
      </label>
      <div class="dev-token-form-actions">
        <button class="btn btn-sm" id="review-token-create" type="button">Create</button>
      </div>
    </div>
    <div id="review-token-list" class="deploy-list">
      <div class="deploy-empty">Loading...</div>
    </div>
  `;
}

function renderReviewTokenDisabled(items: string[]): string {
  return `
    <div class="deploy-card dev-card-disabled">
      <div class="deploy-card-header">
        <div class="deploy-card-title">
          <span class="deploy-status-pill dev-report-status">Planned</span>
          <span class="deploy-card-app">Review token management - coming soon</span>
        </div>
      </div>
      <div class="deploy-card-notes">Planned capabilities:</div>
      <div class="dev-token-planned">
        ${items.map(item => `<div>${esc(item)}</div>`).join('')}
      </div>
    </div>
  `;
}

async function paintBuilds(container: HTMLElement): Promise<void> {
  try {
    const params = new URLSearchParams();
    if (showArchivedBuilds) params.set('include_archived', 'true');
    const builds = await get<Build[]>(`/api/builds?${params}`);
    if (!document.getElementById('builds-list')) return;

    if (builds.length === 0) {
      container.innerHTML = `<div class="deploy-empty">No builds yet. CC will register builds here after completing a frontend build.</div>`;
      return;
    }

    container.innerHTML = builds.map(renderBuildCard).join('');
    wireBuildActions(container);
    void checkTestBuildHealth(container);
  } catch {
    container.innerHTML = `<div class="deploy-empty">Failed to load builds.</div>`;
  }
}

function currentServerId(): string {
  return state.currentServerId ?? state.currentServer ?? '';
}

function currentPersonId(): string {
  return state.ownerId ?? state.people[0]?.id ?? '';
}

function scopedTokenEndpoint(): string {
  return `/api/server/${encodeURIComponent(currentServerId())}/scoped-tokens`;
}

function maskToken(token: string): string {
  return token.length > 8 ? `${token.slice(0, 8)}...` : token;
}

function tokenReviewUrl(token: string): string {
  return `/people/review/${encodeURIComponent(token)}`;
}

function tokenExpiryIso(hours: number): string {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

function wireCellInfraPanel(): void {
  const btn = document.getElementById('btn-rotate-cell-secret');
  const statusEl = document.getElementById('cell-rotate-status');
  const badgesEl = document.getElementById('cell-health-badges');

  btn?.addEventListener('click', async () => {
    if (statusEl) statusEl.textContent = 'Rotating…';
    btn.setAttribute('disabled', 'true');
    try {
      const resp = await post<{ rotated: boolean; hub_people: string; hub_cell: string; hub_cell_error: string | null }>(
        '/api/admin/rotate-cell-secret', {}
      );
      const parts: string[] = [`hub-people: ${resp.hub_people}`];
      if (resp.hub_cell === 'pushed') {
        parts.push('hub-cell: pushed');
      } else {
        parts.push(`hub-cell: ${resp.hub_cell_error || 'needs manual set'}`);
      }
      if (statusEl) statusEl.textContent = parts.join(' · ');
    } catch (e) {
      if (statusEl) statusEl.textContent = `Error: ${e instanceof Error ? e.message : String(e)}`;
    }
    btn.removeAttribute('disabled');
  });

  // Load health badges
  if (badgesEl) {
    void (async () => {
      try {
        const [statusH, callH] = await Promise.all([
          get<{ configured: boolean; source?: string }>('/api/cell-status/health').catch(() => null),
          get<{ configured: boolean; calls_read_only: boolean }>('/api/webhook/call-event/health').catch(() => null),
        ]);
        const dot = (ok: boolean | null) => ok ? '#16a34a' : ok === false ? '#dc2626' : 'var(--fg-muted)';
        const label = (ok: boolean | null) => ok ? 'configured' : ok === false ? 'not set' : 'unknown';
        const badges: string[] = [];
        badges.push(`<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border);border-radius:4px;font-size:11px"><span style="width:6px;height:6px;border-radius:3px;background:${dot(statusH?.configured ?? null)}"></span>Status secret: ${label(statusH?.configured ?? null)}${statusH?.source ? ` (${statusH.source})` : ''}</span>`);
        badges.push(`<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border);border-radius:4px;font-size:11px"><span style="width:6px;height:6px;border-radius:3px;background:${dot(callH?.configured ?? null)}"></span>Call webhook: ${label(callH?.configured ?? null)}</span>`);
        if (callH?.calls_read_only) {
          badges.push(`<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border);border-radius:4px;font-size:11px;background:#dc2626;color:#fff">CALLS READ-ONLY</span>`);
        }
        badgesEl.innerHTML = `<div style="display:flex;flex-wrap:wrap;gap:6px">${badges.join('')}</div>`;
      } catch { /* silent */ }
    })();
  }
}

function wireQuickLinkCopy(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>('.build-public-url').forEach(el => {
    el.addEventListener('click', () => {
      const url = el.dataset['url'] || el.textContent || '';
      void navigator.clipboard.writeText(url).then(() => {
        const original = el.textContent;
        el.textContent = 'Copied';
        setTimeout(() => { el.textContent = original; }, 1500);
      });
    });
  });
}

function wireReviewTokenPanel(container: HTMLElement): void {
  const list = container.querySelector<HTMLElement>('#review-token-list');
  if (!list || !currentServerId()) return;

  void paintReviewTokens(list);

  container.querySelector<HTMLButtonElement>('#review-token-refresh')?.addEventListener('click', () => {
    void paintReviewTokens(list);
  });

  container.querySelector<HTMLButtonElement>('#review-token-show-form')?.addEventListener('click', () => {
    const form = container.querySelector<HTMLElement>('#review-token-form');
    if (form) form.hidden = !form.hidden;
  });

  container.querySelector<HTMLButtonElement>('#review-token-create')?.addEventListener('click', async () => {
    const purpose = container.querySelector<HTMLSelectElement>('#review-token-purpose')?.value as ReviewTokenPurpose | undefined;
    const linked = container.querySelector<HTMLInputElement>('#review-token-linked')?.value.trim() || 'support-v2-draft';
    const expiryRaw = container.querySelector<HTMLSelectElement>('#review-token-expiry')?.value || '24';
    const maxUsesRaw = container.querySelector<HTMLInputElement>('#review-token-max-uses')?.value || '10';
    const maxUses = Math.max(1, Number.parseInt(maxUsesRaw, 10) || 10);
    const expiryHours = Math.max(1, Number.parseInt(expiryRaw, 10) || 24);
    if (!purpose) return;

    const btn = container.querySelector<HTMLButtonElement>('#review-token-create');
    if (btn) btn.disabled = true;
    try {
      const created = await post<CreateScopedTokenResponse>(scopedTokenEndpoint(), {
        purpose,
        max_uses: maxUses,
        linked_record_id: linked,
        linked_record_type: 'test_build',
        metadata: { title: `Review ${linked}` },
        expires_at: tokenExpiryIso(expiryHours),
        created_by: currentPersonId(),
      });
      showCreatedToken(container, created);
      showToast('Review token created', 'ok');
      void paintReviewTokens(list);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Token creation failed', 'err');
    } finally {
      if (btn) btn.disabled = false;
    }
  });
}

function showCreatedToken(container: HTMLElement, created: CreateScopedTokenResponse): void {
  const panel = container.querySelector<HTMLElement>('#review-token-created');
  if (!panel) return;
  const reviewUrl = tokenReviewUrl(created.token);
  panel.hidden = false;
  panel.innerHTML = `
    <div class="dev-token-created-title">Token created. Full token is shown once for copying.</div>
    <div class="build-quick-links-row">
      <span class="build-quick-links-label">Token</span>
      <code class="build-public-url" data-url="${esc(created.token)}">${esc(created.token)}</code>
    </div>
    <div class="build-quick-links-row">
      <span class="build-quick-links-label">Review URL</span>
      <code class="build-public-url" data-url="${esc(reviewUrl)}">${esc(reviewUrl)}</code>
    </div>
  `;
  wireQuickLinkCopy(panel);
}

async function paintReviewTokens(container: HTMLElement): Promise<void> {
  const serverId = currentServerId();
  const personId = currentPersonId();
  if (!serverId) {
    container.innerHTML = `<div class="deploy-empty">Select a server before managing review tokens.</div>`;
    return;
  }
  try {
    const query = personId ? `?person_id=${encodeURIComponent(personId)}` : '';
    const tokens = await get<ScopedToken[]>(`${scopedTokenEndpoint()}${query}`);
    const reviewTokens = tokens.filter(t => t.purpose === 'test_ui_review' || t.purpose === 'ai_review');
    if (reviewTokens.length === 0) {
      container.innerHTML = `<div class="deploy-empty">No review tokens yet.</div>`;
      return;
    }
    container.innerHTML = reviewTokens.map(renderReviewTokenCard).join('');
    wireReviewTokenActions(container);
  } catch {
    container.innerHTML = renderReviewTokenDisabled([
      'List existing scoped review tokens.',
      'Create test UI review and AI review tokens.',
      'Revoke issued tokens and copy scoped review URLs.',
    ]);
  }
}

function renderReviewTokenCard(token: ScopedToken): string {
  const reviewUrl = tokenReviewUrl(token.token);
  return `
    <div class="deploy-card dev-review-card" data-token-id="${esc(token.id)}">
      <div class="deploy-card-header">
        <div class="deploy-card-title">
          <span class="deploy-status-pill dev-token-status-${esc(token.status)}">${esc(token.status)}</span>
          <span class="deploy-card-app">${esc(token.purpose)}</span>
          <span class="deploy-card-hash">${esc(maskToken(token.token))}</span>
        </div>
        <div class="deploy-card-actions">
          <button class="btn btn-sm deploy-action-btn copy-review-url-btn" type="button" data-url="${esc(reviewUrl)}">Copy Review URL</button>
          <button class="btn btn-sm deploy-action-btn revoke-review-token-btn" type="button" data-token-id="${esc(token.id)}" ${token.status === 'revoked' ? 'disabled' : ''}>Revoke</button>
        </div>
      </div>
      <div class="deploy-card-meta">
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Created</span>
          <span class="deploy-meta-value">${esc(fmtDate(token.created_at))}</span>
        </div>
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Expiry</span>
          <span class="deploy-meta-value">${token.expires_at ? esc(fmtDate(token.expires_at)) : 'None'}</span>
        </div>
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Uses</span>
          <span class="deploy-meta-value">${token.use_count} of ${token.max_uses}</span>
        </div>
      </div>
    </div>
  `;
}

function wireReviewTokenActions(container: HTMLElement): void {
  container.querySelectorAll<HTMLButtonElement>('.copy-review-url-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const url = btn.dataset['url'] || '';
      void navigator.clipboard.writeText(url).then(() => showToast('Review URL copied', 'ok'));
    });
  });

  container.querySelectorAll<HTMLButtonElement>('.revoke-review-token-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset['tokenId'] || '';
      if (!id) return;
      btn.disabled = true;
      try {
        await post(`${scopedTokenEndpoint()}/${encodeURIComponent(id)}/revoke`, { person_id: currentPersonId() });
        showToast('Review token revoked', 'ok');
        void paintReviewTokens(container);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Token revoke failed', 'err');
        btn.disabled = false;
      }
    });
  });
}

/** Extract route hints from build notes and render quick-open links. */
function buildQuickLinks(b: Build): string {
  const base = `/people/test/${esc(b.id)}`;
  // Parse routes from notes, looking for patterns like /support-onboarding/test123.
  const routePattern = /\/(support-[a-z-]+(?:\/[a-z0-9-]+)?)/g;
  const routes: { label: string; path: string }[] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = routePattern.exec(b.notes)) !== null) {
    const p = m[1] ?? '';
    if (!p || seen.has(p)) continue;
    seen.add(p);
    // Make a readable label from the path
    const label = p.replace(/-/g, ' ').replace(/\//g, ' › ').replace(/\b\w/g, c => c.toUpperCase());
    routes.push({ label, path: `${base}/${p}` });
  }
  if (routes.length === 0) return '';
  const links = routes.map(r =>
    `<a href="${r.path}" target="_blank" rel="noopener" class="build-quick-link">${esc(r.label)}</a>`
  ).join('');
  const pubUrl = `${location.origin}${base}/`;
  return `
    <div class="build-quick-links">
      <div class="build-quick-links-row">
        <span class="build-quick-links-label">Routes</span>
        ${links}
      </div>
      <div class="build-quick-links-row" style="margin-top:6px">
        <span class="build-quick-links-label">Public URL</span>
        <code class="build-public-url" title="Click to copy" data-url="${esc(pubUrl)}" style="cursor:pointer">${esc(pubUrl)}</code>
      </div>
    </div>
  `;
}

function renderBuildCard(b: Build): string {
  const hashShort = b.git_hash ? b.git_hash.slice(0, 8) : '';
  const sizeKB = Math.round(b.total_size / 1024);
  const statusColor = BUILD_STATUS_COLORS[b.status] || 'var(--fg-muted)';
  const isTest = b.status === 'test';
  const isStaging = b.status === 'staging';
  const isLive = b.status === 'live';
  const isArchived = b.status === 'archived';

  const actions = [];
  actions.push(`<a class="btn btn-sm deploy-action-btn" href="/people/test/${esc(b.id)}/" target="_blank" rel="noopener">Open</a>`);
  if (isTest) {
    actions.push(`<button class="btn btn-sm deploy-action-btn build-action-btn dev-btn-warning" data-build-id="${esc(b.id)}" data-action="promote">Promote to Staging</button>`);
    actions.push(`<button class="btn btn-sm deploy-action-btn build-action-btn" data-build-id="${esc(b.id)}" data-action="archive">Archive</button>`);
  }

  // Health badge: async-checked after render. Shows files-present state.
  const healthId = `build-health-${b.id.replace(/[^a-zA-Z0-9-]/g, '')}`;
  const healthBadge = isTest
    ? `<span id="${esc(healthId)}" class="deploy-health-badge" data-build-id="${esc(b.id)}">checking...</span>`
    : '';
  if (isStaging) {
    actions.push(`<button class="btn btn-sm deploy-action-btn build-action-btn dev-btn-success" data-build-id="${esc(b.id)}" data-action="deploy">Deploy Live</button>`);
    actions.push(`<button class="btn btn-sm deploy-action-btn build-action-btn" data-build-id="${esc(b.id)}" data-action="archive">Archive</button>`);
  }
  if (isLive) {
    actions.push(`<span class="dev-build-live-label">LIVE</span>`);
  }
  if (isArchived) {
    actions.push(`<button class="btn btn-sm deploy-action-btn build-action-btn" data-build-id="${esc(b.id)}" data-action="restore">Restore</button>`);
  }

  const timeline = [];
  timeline.push(`<span>Created ${esc(fmtDate(b.created_at))}</span>`);
  if (b.promoted_at) timeline.push(`<span>Promoted ${esc(fmtDate(b.promoted_at))}</span>`);
  if (b.deployed_at) timeline.push(`<span>Deployed ${esc(fmtDate(b.deployed_at))}</span>`);
  if (b.archived_at) timeline.push(`<span>Archived ${esc(fmtDate(b.archived_at))}</span>`);

  return `
    <div class="deploy-card" data-build-id="${esc(b.id)}">
      <div class="deploy-card-header">
        <div class="deploy-card-title">
          <span class="deploy-status-pill" style="background:${statusColor}">${esc(b.status.toUpperCase())}</span>
          <span class="deploy-card-app">${esc(b.app)}</span>
        </div>
        <div class="deploy-card-actions">${actions.join('')}</div>
      </div>
      ${b.notes ? `<div class="deploy-card-notes">${esc(b.notes)}</div>` : ''}
      ${b.changelog ? `<div class="deploy-card-notes" style="font-style:italic">${esc(b.changelog)}</div>` : ''}
      ${isTest ? buildQuickLinks(b) : ''}
      <div class="deploy-card-meta">
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Name</span>
          <span class="deploy-meta-value">${esc(b.id)} ${healthBadge}</span>
        </div>
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Version</span>
          <span class="deploy-meta-value">${esc(b.version || 'Unversioned')}</span>
        </div>
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Creator</span>
          <span class="deploy-meta-value">${esc(b.creator)}</span>
        </div>
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Hash</span>
          <span class="deploy-meta-value">${esc(hashShort || 'No hash')}</span>
        </div>
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Date</span>
          <span class="deploy-meta-value">${esc(fmtDate(b.created_at))}</span>
        </div>
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Notes</span>
          <span class="deploy-meta-value">${esc(b.notes || 'No notes')}</span>
        </div>
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Size</span>
          <span class="deploy-meta-value">${b.file_count} files, ${sizeKB} KB</span>
        </div>
        <div class="deploy-timeline">${timeline.map(t => `<div class="deploy-timeline-item">${t}</div>`).join('')}</div>
      </div>
    </div>
  `;
}

/** Async health check for each test build root to verify files exist. */
async function checkTestBuildHealth(container: HTMLElement): Promise<void> {
  const badges = container.querySelectorAll<HTMLElement>('.deploy-health-badge');
  for (const badge of badges) {
    const buildId = badge.dataset['buildId'] ?? '';
    if (!buildId) continue;
    try {
      const res = await fetch(`/people/test/${encodeURIComponent(buildId)}/`, { method: 'HEAD' });
      if (res.ok) {
        badge.textContent = 'files ok';
        badge.style.background = 'var(--success)';
        badge.style.color = 'var(--bg)';
      } else {
        badge.textContent = 'files missing';
        badge.style.background = 'var(--danger)';
        badge.style.color = 'var(--bg)';
        badge.title = `HEAD /people/test/${buildId}/ returned ${res.status}. Build files may have been lost during container recreation.`;
      }
    } catch {
      badge.textContent = 'check failed';
      badge.style.background = 'var(--warning)';
      badge.style.color = 'var(--bg)';
    }
  }
}

function wireBuildActions(container: HTMLElement): void {
  container.querySelectorAll<HTMLButtonElement>('.build-action-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = btn.dataset['buildId'] || '';
      const action = btn.dataset['action'] || '';
      if (!id || !action) return;

      if (action === 'deploy' && !confirm('Deploy this build to LIVE? This replaces the current frontend for all users.')) return;
      if (action === 'promote' && !confirm('Promote this build to staging? It will be available for live deployment.')) return;

      btn.disabled = true;
      try {
        if (action === 'promote') {
          await post(`/api/builds/${encodeURIComponent(id)}/promote`, {});
          showToast('Build promoted to staging', 'ok');
        } else if (action === 'deploy') {
          await post(`/api/builds/${encodeURIComponent(id)}/deploy`, {});
          showToast('Build deployed LIVE', 'ok');
        } else if (action === 'archive') {
          await post(`/api/builds/${encodeURIComponent(id)}/archive`, {});
          showToast('Build archived (files preserved)', 'ok');
        } else if (action === 'restore') {
          await post(`/api/builds/${encodeURIComponent(id)}/restore`, {});
          showToast('Build restored to test', 'ok');
        }
        const list = document.getElementById('builds-list');
        if (list) void paintBuilds(list);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Action failed', 'err');
        btn.disabled = false;
      }
    });
  });

  // Copy-to-clipboard on public URL codes
  container.querySelectorAll<HTMLElement>('.build-public-url').forEach(el => {
    el.addEventListener('click', () => {
      const url = el.dataset['url'] || el.textContent || '';
      void navigator.clipboard.writeText(url).then(() => {
        const orig = el.textContent;
        el.textContent = 'Copied!';
        setTimeout(() => { el.textContent = orig; }, 1500);
      });
    });
  });
}

async function loadStagedPreview(): Promise<void> {
  const el = document.getElementById('dev-staged-preview');
  if (!el) return;
  try {
    const info = await get<StagedInfo>('/api/deploys/staged-preview');
    if (!info.has_staged) {
      el.innerHTML = '';
      return;
    }
    const sizeKB = Math.round(info.total_size / 1024);
    el.innerHTML = `
      <div class="deploy-staged-card">
        <div class="deploy-staged-header">
          <span class="deploy-staged-dot"></span>
          <strong>Staged build ready</strong>
          <span class="deploy-staged-size">${info.files.length} files, ${sizeKB} KB</span>
        </div>
        <div class="deploy-staged-files">
          ${info.files.slice(0, 10).map(f => `<div class="deploy-staged-file">${esc(f.path)} <span style="color:var(--fg-muted)">${Math.round(f.size / 1024)}KB</span></div>`).join('')}
          ${info.files.length > 10 ? `<div class="deploy-staged-file" style="color:var(--fg-muted)">+${info.files.length - 10} more</div>` : ''}
        </div>
      </div>
    `;
  } catch {
    el.innerHTML = '';
  }
}

/* ── Preflight tab ──────────────────────────────────────────────── */

interface PreflightReport {
  timestamp: string;
  pass: boolean;
  issues: string[];
  db: {
    path: string;
    exists: boolean;
    size_bytes: number;
    size_human: string;
    writable: boolean;
  };
  migrations: {
    dir_exists: boolean;
    dir_path: string;
    files_count: number;
    applied_count: number;
    pending: string[];
    missing_from_disk: string[];
    duplicate_prefixes: { prefix: string; files: string[] }[];
    semicolon_risks: { file: string; line: number; context: string }[];
    latest_number: string;
    out_of_order: string[];
  };
  binary: {
    path: string;
    expected_name: string;
    name_matches: boolean;
    exists: boolean;
    size_bytes: number;
    size_human: string;
    modified: string;
  };
  routes: {
    health: { path: string; ok: boolean; status_code: number };
    builds: { path: string; ok: boolean; status_code: number };
    deploys: { path: string; ok: boolean; status_code: number };
    test_index: { path: string; ok: boolean; status_code: number };
    ai_tasks: { path: string; ok: boolean; status_code: number };
  };
  process: {
    pid: number;
    uptime_seconds: number | null;
    uptime_human: string;
    binary_in_proc: string;
  };
}

// ── Quick Links + Reports (merged from former Review Links tab) ──────
interface QuickLink {
  title: string;
  description: string;
  href: string;
  badge: string;
  disabled?: boolean;
}

const QUICK_LINKS: QuickLink[] = [
  {
    title: 'Ma Guest Preview',
    description: 'Auth-gated mock review of Ma guest experience: PIN create/verify, disabled/request, home, text (reply, read receipts), call (5 states), screenshot/retention/security notices.',
    href: '/people/test/guest-preview/ma',
    badge: 'Test UI',
  },
  {
    title: 'James Guest Preview',
    description: 'Auth-gated mock review of James guest experience: PIN create/verify, disabled/request, home, text (reply, read receipts), call (5 states), screenshot/retention/security notices.',
    href: '/people/test/guest-preview/james',
    badge: 'Test UI',
  },
  {
    title: 'Guest Controls',
    description: 'Mitchell controls for Ma and James: generate/copy/disable/enable/regenerate links, reset PIN, approve/deny requests, category settings.',
    href: '/people/test/guest-controls',
    badge: 'Test UI',
  },
  {
    title: 'Migration Dashboard',
    description: 'Windows to Hub People migration plan: auth hardening, Gitea/VPS workspace, code-server, deploy worker, Docker, terminal, noVNC, backup, CC/Codex. All preview/mock.',
    href: '/people/test/admin-migration-dashboard',
    badge: 'Admin',
  },
  {
    title: 'NDIS Plan Extraction',
    description: 'Upload and extract NDIS plan budgets, categories, and support items.',
    href: '/people/test/support-v2-draft/ndis-extraction',
    badge: 'Test UI',
  },
  {
    title: 'Support Invoice Generator',
    description: 'Manual NDIS-aware support invoice draft with session entry, participant-specific codes for line items, validation, preview, and test email. Draft/manual first.',
    href: '/people/test/support-v2-draft/ndis-invoice-draft',
    badge: 'Test UI',
  },
  {
    title: 'Provider Invoice Review',
    description: 'V0.1 provider review flow — confirm invoice, update billing identity, report issues. Test/mock only.',
    href: '/people/test/support-v2-draft/provider-invoice-review',
    badge: 'Test UI',
  },
  {
    title: 'Support Profile Setup',
    description: 'Public support profile creation flow for providers.',
    href: '/people/test/support-v2-draft/support-profile/setup',
    badge: 'Test UI',
  },
  {
    title: 'Support Onboarding',
    description: 'Combined onboarding flow for new support participants.',
    href: '/people/test/support-v2-draft/support-onboarding/test123',
    badge: 'Test UI',
  },
  {
    title: 'Support Agreement Draft',
    description: 'Service agreement drafting and review surface.',
    href: '/people/test/support-v2-draft/support-agreement/draft',
    badge: 'Test UI',
  },
  {
    title: 'Support Onboarding Demo',
    description: 'Hub People workspace demo showing fake support onboarding result data. Demo only - no backend, no storage, no invoicing.',
    href: '/people/test/support-v2-draft/support-onboarding-demo',
    badge: 'Demo',
  },
  {
    title: 'Support Worker Onboarding',
    description: 'Mock support worker/provider onboarding form with simulated submission. Demo only - no backend storage.',
    href: '/people/test/support-v2-draft/support-worker-onboarding',
    badge: 'Test UI',
  },
  {
    title: 'Participant Profile Builder',
    description: 'Save participant details, plan context, and applicable NDIS line codes. Test/demo only -- no production storage.',
    href: '/people/test/support-v2-draft/participant-profile-builder',
    badge: 'Test',
  },
  {
    title: 'Voice Flow Builder — test build (un-wired rough-in)',
    description: 'Preview of the Voice Flow Builder (Calls > Automation): two-column editor that compiles a voice-flow tree to DSL with live syntax highlighting and copy-to-clipboard. Un-wired rough-in — client-side only, no save/load.',
    href: '/flow-builder/',
    badge: 'Test UI',
  },
  {
    title: 'Provider Profile Builder',
    description: 'Build/edit provider/support worker profiles with billing identity types for invoicing. Test/demo only -- no production storage.',
    href: '/people/test/support-v2-draft/provider-profile-builder',
    badge: 'Test',
  },
];

interface ReportLink {
  title: string;
  date: string;
  description: string;
  badge: string;
}

const REPORT_LINKS: ReportLink[] = [
  { title: 'Deploy Verification Hardening', date: '31 May 2026', description: 'Staged deploy preflight, verification, and rollback improvements.', badge: 'Report' },
  { title: 'Route/Access Correction', date: '1 Jun 2026', description: 'Route classification and access-gating corrections for public, auth, and scoped-token surfaces.', badge: 'Report' },
  { title: 'Hub Core Migration Plan', date: '1 Jun 2026', description: 'Migration plan for hub-core boundaries, proxying, and app integration.', badge: 'Report' },
  { title: 'NDIS Reference/Extractor', date: '31 May 2026', description: 'NDIS plan extraction analysis, reference model, and catalogue import design.', badge: 'Report' },
  { title: 'Network/Wi-Fi Report', date: '31 May 2026', description: 'DNS, TLS, Cloudflare tunnel, and enterprise network analysis.', badge: 'Report' },
  { title: 'UI Integration Report', date: '31 May 2026', description: 'Development channel tabs, category creation, modal height, and email compose fixes.', badge: 'Report' },
];

// ── Preflight Tab ─────────────────────────────────────────────────────
let preflightData: PreflightReport | null = null;
let preflightLoading = false;
let preflightError = '';

function renderPreflightTab(container: HTMLElement): void {
  container.innerHTML = `
    <div class="deploy-toolbar">
      <h2 class="deploy-heading">Deploy Preflight</h2>
      <div class="deploy-toolbar-right">
        <button class="btn btn-sm dev-btn-success" id="preflight-run-btn">Run Preflight Check</button>
      </div>
    </div>
    <div id="preflight-result"></div>
  `;

  const resultEl = document.getElementById('preflight-result') as HTMLElement;
  const runBtn = document.getElementById('preflight-run-btn') as HTMLButtonElement;

  if (preflightData) {
    renderPreflightResult(resultEl, preflightData);
  } else {
    resultEl.innerHTML = `<div class="deploy-empty">Click "Run Preflight Check" to verify deployment state.</div>`;
  }

  runBtn.addEventListener('click', async () => {
    if (preflightLoading) return;
    preflightLoading = true;
    preflightError = '';
    runBtn.disabled = true;
    runBtn.textContent = 'Checking...';
    resultEl.innerHTML = `<div class="deploy-empty">Running preflight checks...</div>`;

    try {
      preflightData = await get<PreflightReport>('/api/deploy-preflight');
      renderPreflightResult(resultEl, preflightData);
    } catch (err) {
      preflightError = err instanceof Error ? err.message : 'Preflight check failed';
      resultEl.innerHTML = `<div class="preflight-error">${esc(preflightError)}</div>`;
    } finally {
      preflightLoading = false;
      runBtn.disabled = false;
      runBtn.textContent = 'Run Preflight Check';
    }
  });
}

function checkIcon(ok: boolean): string {
  return ok
    ? `<span class="preflight-icon preflight-pass" title="Pass">&#x2713;</span>`
    : `<span class="preflight-icon preflight-fail" title="Fail">&#x2717;</span>`;
}

function warnIcon(): string {
  return `<span class="preflight-icon preflight-warn" title="Warning (non-blocking)">!</span>`;
}

function renderPreflightResult(el: HTMLElement, r: PreflightReport): void {
  const passClass = r.pass ? 'preflight-banner-pass' : 'preflight-banner-fail';
  const passText = r.pass
    ? `ALL CHECKS PASSED &mdash; safe to deploy`
    : `${r.issues.length} BLOCKING ISSUE(S) &mdash; fix before deploying`;

  const routeRows = [r.routes.health, r.routes.builds, r.routes.deploys, r.routes.test_index, r.routes.ai_tasks]
    .map(rt => `<tr><td>${checkIcon(rt.ok)}</td><td class="preflight-mono">${esc(rt.path)}</td><td>${rt.status_code}</td></tr>`)
    .join('');

  const hasDupWarnings = r.migrations.duplicate_prefixes.length > 0;
  const hasSemiWarnings = r.migrations.semicolon_risks.length > 0;
  const hasWarnings = hasDupWarnings || hasSemiWarnings;

  el.innerHTML = `
    <div class="preflight-banner ${passClass}">${passText}</div>
    <div class="preflight-meta">Checked ${esc(r.timestamp)} &middot; PID ${r.process.pid} &middot; Uptime ${esc(r.process.uptime_human)}</div>

    ${r.issues.length > 0 ? `
      <div class="preflight-section preflight-section-blocking">
        <div class="preflight-section-title">Blocking Issues</div>
        <div class="preflight-section-subtitle">Must be resolved before deployment</div>
        <div class="preflight-issues">
          ${r.issues.map(i => `<div class="preflight-issue">${esc(i)}</div>`).join('')}
        </div>
      </div>
    ` : ''}

    ${hasWarnings ? `
      <div class="preflight-section preflight-section-warn">
        <div class="preflight-section-title">Non-Blocking Warnings</div>
        <div class="preflight-section-subtitle">Historical issues in already-applied migrations. Safe to deploy.</div>
        <div class="preflight-warnings">
          ${hasDupWarnings ? r.migrations.duplicate_prefixes.map(d =>
            `<div class="preflight-warning">Duplicate prefix ${esc(d.prefix)}: ${d.files.map(f => esc(f)).join(', ')}</div>`
          ).join('') : ''}
          ${hasSemiWarnings ? `<div class="preflight-warning">${r.migrations.semicolon_risks.length} semicolon(s) in SQL string literals of applied migrations</div>` : ''}
        </div>
      </div>
    ` : ''}

    <div class="preflight-grid">
      <div class="preflight-section">
        <div class="preflight-section-title">Database</div>
        <table class="preflight-table">
          <tr><td>${checkIcon(r.db.exists)}</td><td>File exists</td><td class="preflight-mono">${esc(r.db.path)}</td></tr>
          <tr><td>${checkIcon(r.db.size_bytes > 0)}</td><td>Size</td><td>${esc(r.db.size_human)}</td></tr>
          <tr><td>${checkIcon(r.db.writable)}</td><td>Writable</td><td>${r.db.writable ? 'Yes' : 'No'}</td></tr>
        </table>
      </div>

      <div class="preflight-section">
        <div class="preflight-section-title">Binary</div>
        <table class="preflight-table">
          <tr><td>${checkIcon(r.binary.exists)}</td><td>Exists</td><td class="preflight-mono">${esc(r.binary.path)}</td></tr>
          <tr><td>${checkIcon(r.binary.name_matches)}</td><td>Name match</td><td>${esc(r.binary.expected_name)}</td></tr>
          <tr><td></td><td>Size</td><td>${esc(r.binary.size_human)}</td></tr>
          <tr><td></td><td>Modified</td><td>${esc(r.binary.modified || 'unknown')}</td></tr>
          <tr><td></td><td>Proc exe</td><td class="preflight-mono">${esc(r.process.binary_in_proc)}</td></tr>
        </table>
      </div>

      <div class="preflight-section">
        <div class="preflight-section-title">Migrations</div>
        <table class="preflight-table">
          <tr><td>${checkIcon(r.migrations.dir_exists)}</td><td>Directory</td><td class="preflight-mono">${esc(r.migrations.dir_path)}</td></tr>
          <tr><td>${checkIcon(r.migrations.files_count > 0)}</td><td>Files on disk</td><td>${r.migrations.files_count}</td></tr>
          <tr><td></td><td>Applied in DB</td><td>${r.migrations.applied_count}</td></tr>
          <tr><td>${checkIcon(r.migrations.pending.length === 0)}</td><td>Pending</td><td>${r.migrations.pending.length > 0 ? esc(r.migrations.pending.join(', ')) : 'None'}</td></tr>
          <tr><td>${checkIcon(r.migrations.missing_from_disk.length === 0)}</td><td>Missing from disk</td><td>${r.migrations.missing_from_disk.length > 0 ? esc(r.migrations.missing_from_disk.join(', ')) : 'None'}</td></tr>
          <tr><td>${hasDupWarnings ? warnIcon() : checkIcon(true)}</td><td>Duplicate prefixes</td><td>${hasDupWarnings ? r.migrations.duplicate_prefixes.map(d => esc(d.prefix)).join(', ') + ' (applied, non-blocking)' : 'None'}</td></tr>
          <tr><td>${hasSemiWarnings ? warnIcon() : checkIcon(true)}</td><td>Semicolon risks</td><td>${hasSemiWarnings ? r.migrations.semicolon_risks.length + ' in applied migrations (non-blocking)' : 'None'}</td></tr>
          <tr><td></td><td>Latest number</td><td>${esc(r.migrations.latest_number)}</td></tr>
        </table>
      </div>

      <div class="preflight-section">
        <div class="preflight-section-title">Routes</div>
        <table class="preflight-table">
          ${routeRows}
        </table>
      </div>
    </div>

    <div class="preflight-manual">
      <div class="preflight-section-title">Manual Verification Still Required</div>
      <div class="preflight-manual-list">
        Docker image version &middot; Correct production DB (not a stale copy) &middot; Cargo dependency changes &middot; Network connectivity (hub-core to hub-people) &middot; SSL/TLS termination
      </div>
    </div>
  `;
}

/* ── Tunnels tab ───────────────────────────────────────────────── */

interface Tunnel {
  id: string;
  name: string;
  target_url: string;
  tunnel_url: string;
  pid: number;
  status: string;
  created_at: string;
  updated_at: string;
}

let tunnelPollTimer: ReturnType<typeof setInterval> | null = null;

function renderTunnelsTab(container: HTMLElement): void {
  container.innerHTML = `
    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">Tunnel Management</h2>
          <p>Cloudflared tunnels for exposing internal services. Start a tunnel to get a public URL for git push, testing, or external access.</p>
        </div>
        <div class="deploy-toolbar-right">
          <button class="btn btn-secondary btn-sm" id="tunnel-refresh-btn" type="button">Refresh</button>
        </div>
      </div>
      <div id="tunnel-list" class="deploy-list">
        <div class="deploy-empty">Loading...</div>
      </div>
    </div>

    <div class="dev-section-card">
      <div class="dev-section-heading">
        <div>
          <h2 class="deploy-heading">New Tunnel</h2>
          <p>Create a named tunnel profile pointing at an internal service.</p>
        </div>
      </div>
      <div class="tunnel-new-form">
        <label>
          <span>Name</span>
          <input id="tunnel-new-name" type="text" placeholder="e.g. hub-cell" />
        </label>
        <label>
          <span>Target URL</span>
          <input id="tunnel-new-target" type="text" placeholder="e.g. http://hub-cell:3020" />
        </label>
        <div class="dev-token-form-actions">
          <button class="btn btn-sm" id="tunnel-create-btn" type="button">Create</button>
        </div>
      </div>
    </div>
  `;

  const listEl = document.getElementById('tunnel-list') as HTMLElement;
  void paintTunnels(listEl);

  document.getElementById('tunnel-refresh-btn')?.addEventListener('click', () => {
    void paintTunnels(listEl);
  });

  document.getElementById('tunnel-create-btn')?.addEventListener('click', async () => {
    const nameInput = document.getElementById('tunnel-new-name') as HTMLInputElement;
    const targetInput = document.getElementById('tunnel-new-target') as HTMLInputElement;
    const name = nameInput.value.trim();
    const target = targetInput.value.trim();
    if (!name || !target) {
      showToast('Name and target URL required', 'err');
      return;
    }
    try {
      await post('/api/tunnels', { name, target_url: target });
      showToast(`Tunnel "${name}" created`, 'ok');
      nameInput.value = '';
      targetInput.value = '';
      void paintTunnels(listEl);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Create failed', 'err');
    }
  });

  if (tunnelPollTimer) clearInterval(tunnelPollTimer);
  tunnelPollTimer = setInterval(() => {
    if (!document.getElementById('tunnel-list')) {
      if (tunnelPollTimer) { clearInterval(tunnelPollTimer); tunnelPollTimer = null; }
      return;
    }
    void paintTunnels(listEl);
  }, 10_000);
}

async function paintTunnels(container: HTMLElement): Promise<void> {
  try {
    const tunnels = await get<Tunnel[]>('/api/tunnels');
    if (!document.getElementById('tunnel-list')) return;

    if (tunnels.length === 0) {
      container.innerHTML = `<div class="deploy-empty">No tunnels configured. Create one below.</div>`;
      return;
    }

    container.innerHTML = tunnels.map(renderTunnelCard).join('');
    wireTunnelActions(container);
  } catch {
    container.innerHTML = `<div class="deploy-empty">Failed to load tunnels.</div>`;
  }
}

function tunnelStatusDot(status: string): string {
  const colors: Record<string, string> = {
    running: 'var(--success)',
    stopped: 'var(--fg-muted)',
    dead: 'var(--danger)',
    starting: 'var(--warning)',
  };
  const color = colors[status] || 'var(--fg-muted)';
  return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color}"></span>`;
}

function renderTunnelCard(t: Tunnel): string {
  const isRunning = t.status === 'running';
  const isStopped = t.status === 'stopped' || t.status === 'dead';
  const statusColor = isRunning ? 'var(--success)' : t.status === 'dead' ? 'var(--danger)' : 'var(--fg-muted)';

  const actions: string[] = [];
  if (isStopped) {
    actions.push(`<button class="btn btn-sm deploy-action-btn dev-btn-success tunnel-action-btn" data-tunnel="${esc(t.name)}" data-action="start">Start</button>`);
    actions.push(`<button class="btn btn-sm btn-danger-ghost deploy-action-btn tunnel-action-btn" data-tunnel="${esc(t.name)}" data-action="delete">Delete</button>`);
  }
  if (isRunning) {
    actions.push(`<button class="btn btn-sm deploy-action-btn tunnel-action-btn" data-tunnel="${esc(t.name)}" data-action="stop">Stop</button>`);
    actions.push(`<button class="btn btn-sm btn-danger-ghost deploy-action-btn tunnel-action-btn" data-tunnel="${esc(t.name)}" data-action="kill">Kill</button>`);
  }

  const urlRow = t.tunnel_url && t.tunnel_url !== 'starting...'
    ? `<div class="build-quick-links-row" style="margin-top:6px">
        <span class="build-quick-links-label">Public URL</span>
        <code class="build-public-url tunnel-url-copy" title="Click to copy" data-url="${esc(t.tunnel_url)}" style="cursor:pointer">${esc(t.tunnel_url)}</code>
      </div>`
    : t.tunnel_url === 'starting...'
    ? `<div style="font-size:12px;color:var(--warning);margin-top:6px">Waiting for cloudflared URL...</div>`
    : '';

  return `
    <div class="deploy-card tunnel-card" data-tunnel-name="${esc(t.name)}">
      <div class="deploy-card-header">
        <div class="deploy-card-title">
          ${tunnelStatusDot(t.status)}
          <span class="deploy-status-pill" style="background:${statusColor}">${esc(t.status)}</span>
          <span class="deploy-card-app">${esc(t.name)}</span>
        </div>
        <div class="deploy-card-actions">${actions.join('')}</div>
      </div>
      <div class="deploy-card-meta">
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Target</span>
          <span class="deploy-meta-value" style="font-family:monospace">${esc(t.target_url)}</span>
        </div>
        ${t.pid > 0 ? `<div class="deploy-meta-row">
          <span class="deploy-meta-label">PID</span>
          <span class="deploy-meta-value">${t.pid}</span>
        </div>` : ''}
        <div class="deploy-meta-row">
          <span class="deploy-meta-label">Updated</span>
          <span class="deploy-meta-value">${esc(fmtDate(t.updated_at))}</span>
        </div>
      </div>
      ${urlRow}
    </div>
  `;
}

function wireTunnelActions(container: HTMLElement): void {
  container.querySelectorAll<HTMLButtonElement>('.tunnel-action-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const name = btn.dataset['tunnel'] || '';
      const action = btn.dataset['action'] || '';
      if (!name || !action) return;

      if (action === 'delete' && !confirm(`Delete tunnel "${name}"?`)) return;
      if (action === 'kill' && !confirm(`Force kill tunnel "${name}"?`)) return;

      btn.disabled = true;
      try {
        if (action === 'start') {
          await post(`/api/tunnels/${encodeURIComponent(name)}/start`, {});
          showToast(`Tunnel "${name}" starting`, 'ok');
        } else if (action === 'stop') {
          await post(`/api/tunnels/${encodeURIComponent(name)}/stop`, {});
          showToast(`Tunnel "${name}" stopped`, 'ok');
        } else if (action === 'kill') {
          await post(`/api/tunnels/${encodeURIComponent(name)}/kill`, {});
          showToast(`Tunnel "${name}" killed`, 'ok');
        } else if (action === 'delete') {
          await del(`/api/tunnels/${encodeURIComponent(name)}`);
          showToast(`Tunnel "${name}" deleted`, 'ok');
        }
        const list = document.getElementById('tunnel-list');
        if (list) void paintTunnels(list);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Action failed', 'err');
        btn.disabled = false;
      }
    });
  });

  container.querySelectorAll<HTMLElement>('.tunnel-url-copy').forEach(el => {
    el.addEventListener('click', () => {
      const url = el.dataset['url'] || el.textContent || '';
      void navigator.clipboard.writeText(url).then(() => {
        const orig = el.textContent;
        el.textContent = 'Copied!';
        setTimeout(() => { el.textContent = orig; }, 1500);
      });
    });
  });
}

/* ── staged preview styles ───────────────────────────────────────── */
const stagedStyleId = 'deploy-staged-styles';
if (!document.getElementById(stagedStyleId)) {
  const s = document.createElement('style');
  s.id = stagedStyleId;
  s.textContent = `
.dev-tabs { display: flex; gap: 0; margin-bottom: 16px; border-bottom: 1px solid var(--border); }
.dev-tab { padding: 8px 20px; border: none; background: transparent; color: var(--fg-muted); font-size: 13px; font-weight: 600; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; }
.dev-tab:hover { color: var(--fg); }
.dev-tab-active { color: var(--fg); border-bottom-color: var(--accent); }
.deploy-staged-preview { margin-bottom: 12px; }
.deploy-staged-card { padding: 14px; border-radius: 8px; border: 2px dashed var(--accent); background: var(--bg-secondary); }
.deploy-staged-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 14px; }
.deploy-staged-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); animation: staged-pulse 2s ease-in-out infinite; }
@keyframes staged-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
.deploy-staged-size { font-size: 12px; color: var(--fg-muted); margin-left: auto; }
.deploy-staged-files { display: flex; flex-direction: column; gap: 2px; }
.deploy-staged-file { font-size: 12px; font-family: monospace; color: var(--fg); padding: 2px 0; }
.preflight-banner { padding: 12px 16px; border-radius: 8px; font-weight: 700; font-size: 14px; margin-bottom: 12px; text-align: center; }
.preflight-banner-pass { background: var(--bg-secondary); color: var(--success); border: 1px solid var(--success); }
.preflight-banner-fail { background: var(--bg-secondary); color: var(--danger); border: 1px solid var(--danger); }
.preflight-meta { font-size: 12px; color: var(--fg-muted); margin-bottom: 16px; text-align: center; }
.preflight-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 900px) { .preflight-grid { grid-template-columns: 1fr; } }
.preflight-section { background: var(--bg-secondary); border-radius: 8px; padding: 14px; border: 1px solid var(--border); }
.preflight-section-title { font-weight: 700; font-size: 13px; margin-bottom: 8px; color: var(--fg); }
.preflight-table { width: 100%; font-size: 12px; border-collapse: collapse; }
.preflight-table td { padding: 3px 6px; vertical-align: top; color: var(--fg); }
.preflight-table td:first-child { width: 20px; text-align: center; }
.preflight-mono { font-family: monospace; font-size: 11px; word-break: break-all; }
.preflight-icon { font-weight: 700; font-size: 13px; }
.preflight-pass { color: var(--success); }
.preflight-fail { color: var(--danger); }
.preflight-issues { display: flex; flex-direction: column; gap: 4px; }
.preflight-issue { font-size: 12px; color: var(--danger); padding: 6px 10px; background: var(--bg-tertiary); border-radius: 4px; font-family: monospace; }
.preflight-error { padding: 16px; text-align: center; color: var(--danger); font-size: 13px; }
.preflight-warn { color: var(--warning); font-weight: 700; }
.preflight-section-subtitle { font-size: 11px; color: var(--fg-muted); margin-bottom: 8px; }
.preflight-section-blocking { border-color: var(--danger); }
.preflight-section-warn { border-color: var(--warning); }
.preflight-warnings { display: flex; flex-direction: column; gap: 4px; }
.preflight-warning { font-size: 12px; color: var(--warning); padding: 6px 10px; background: var(--bg-tertiary); border-radius: 4px; font-family: monospace; }
.preflight-manual { margin-top: 12px; padding: 12px 14px; border-radius: 8px; background: var(--bg-secondary); border: 1px dashed var(--border); }
.preflight-manual-list { font-size: 12px; color: var(--fg-muted); margin-top: 4px; line-height: 1.6; }
.dev-test-next { display: grid; grid-template-columns: minmax(180px, 0.7fr) minmax(0, 1.3fr); gap: 14px; padding: 14px; margin-bottom: 18px; border: 1px solid var(--accent); border-radius: 8px; background: var(--bg-secondary); }
.dev-test-next h2 { margin: 0 0 6px; color: var(--fg); font-size: 17px; }
.dev-test-next p { margin: 0; color: var(--fg-muted); font-size: 12px; line-height: 1.5; }
.dev-test-next-links { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.dev-test-next-link { display: grid; gap: 5px; padding: 10px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg-tertiary); color: var(--fg); text-decoration: none; min-width: 0; }
.dev-test-next-link strong { font-size: 13px; color: var(--fg); }
.dev-test-next-link span:last-child { color: var(--fg-muted); font-size: 12px; line-height: 1.4; }
.dev-section-card { margin-bottom: 18px; padding: 14px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-secondary); }
.dev-section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.dev-section-heading .deploy-heading { margin-bottom: 4px; }
.dev-section-heading p { margin: 0; color: var(--fg-muted); font-size: 12px; line-height: 1.5; }
.dev-card-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; }
@media (max-width: 768px) { .dev-card-grid, .dev-test-next, .dev-test-next-links { grid-template-columns: 1fr; } .dev-section-heading { flex-direction: column; } }
.dev-card-disabled { opacity: 0.68; }
.dev-card-disabled button { cursor: default; }
.dev-badge-test-ui { background: var(--accent); }
.dev-badge-demo { background: var(--success); }
.dev-badge-read-only, .dev-badge-report { background: var(--fg-muted); }
.dev-badge-planned { background: var(--warning); }
.dev-review-status { background: var(--accent); }
.dev-report-status { background: var(--fg-muted); }
.dev-token-warning, .dev-warning { color: var(--warning); font-size: 12px; line-height: 1.5; }
.dev-token-toolbar { display: flex; gap: 8px; justify-content: flex-end; margin: 10px 0; }
.dev-token-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; padding: 12px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg-tertiary); margin-bottom: 12px; }
.dev-token-form label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--fg-muted); }
.dev-token-form input, .dev-token-form select { background: var(--bg); color: var(--fg); border: 1px solid var(--border); border-radius: 6px; padding: 7px 8px; }
.dev-token-form-actions { display: flex; align-items: end; }
.dev-token-created { margin: 10px 0; padding: 12px; border: 1px solid var(--success); border-radius: 8px; background: var(--bg-tertiary); }
.dev-token-created-title { font-weight: 700; font-size: 12px; color: var(--success); margin-bottom: 8px; }
.dev-token-planned { display: grid; gap: 4px; margin-top: 8px; font-size: 12px; color: var(--fg-muted); }
.dev-token-status-active { background: var(--success); }
.dev-token-status-expired, .dev-token-status-used { background: var(--warning); }
.dev-token-status-revoked { background: var(--danger); }
.dev-verification-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; margin-bottom: 12px; }
.dev-verification-item { display: flex; justify-content: space-between; gap: 10px; padding: 10px; border-radius: 8px; background: var(--bg-tertiary); border: 1px solid var(--border); }
.dev-warning-list { display: grid; gap: 6px; }
.deploy-health-badge { font-size: 11px; padding: 1px 6px; border-radius: 3px; background: var(--bg-tertiary); color: var(--fg-muted); }
.dev-btn-warning { background: var(--warning); color: var(--bg); font-weight: 600; }
.dev-btn-success { background: var(--success); color: var(--bg); font-weight: 700; }
.dev-build-live-label { font-size: 12px; color: var(--success); font-weight: 700; }
.tunnel-card { transition: border-color 0.2s; }
.tunnel-card:hover { border-color: var(--accent); }
.tunnel-new-form { display: grid; grid-template-columns: 1fr 2fr auto; gap: 10px; align-items: end; padding: 12px; }
.tunnel-new-form label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--fg-muted); }
.tunnel-new-form input { background: var(--bg); color: var(--fg); border: 1px solid var(--border); border-radius: 6px; padding: 7px 8px; }
@media (max-width: 768px) { .tunnel-new-form { grid-template-columns: 1fr; } }
`;
  document.head.appendChild(s);
}
